import { ArrowUpRight, Brain, Radar, Scale, Target } from 'lucide-react';
import { Badge } from '../../components/common/Badge';
import { formatPct } from '../../utils/format';
import { MeterRow, MetricCard, WorkspacePanel } from './PlatformWorkspace';
import { usePlatformTelemetry } from '../../hooks/usePlatformTelemetry';

export function PlatformAnalyticsPage() {
  const { data, loading } = usePlatformTelemetry();

  if (loading) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center h-10 px-4 border-b border-surface-4 bg-surface-1 flex-shrink-0">
          <h1 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Platform Analytics</h1>
        </div>
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
          Loading platform telemetry…
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center h-10 px-4 border-b border-surface-4 bg-surface-1 flex-shrink-0">
          <h1 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Platform Analytics</h1>
        </div>
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
          Platform telemetry unavailable. Check back shortly.
        </div>
      </div>
    );
  }

  const {
    modules: platformModules,
    portfolioSummary,
    validationSummary,
    analyticsExecutiveSummary,
    confidenceDistribution,
    confidenceDrift,
    recommendationTypeRows,
  } = data;

  const leaderboard = [...platformModules].sort((left, right) => right.roi - left.roi);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center h-10 px-4 border-b border-surface-4 bg-surface-1 flex-shrink-0">
        <h1 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Platform Analytics</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricCard
            label="Daily Pulse summary"
            value={`${validationSummary.totalReleasedRecommendations} signals`}
            detail={analyticsExecutiveSummary.dailyPulse}
            tone="info"
          />
          <MetricCard
            label="Weekly ecosystem summary"
            value={formatPct(portfolioSummary.weightedROI)}
            detail={analyticsExecutiveSummary.weeklySummary}
            tone="win"
          />
          <MetricCard
            label="Calibration health"
            value={formatPct(
              platformModules.length > 0
                ? platformModules.reduce((total, module) => total + module.calibrationScore, 0) /
                    platformModules.length
                : 0,
            )}
            detail="Confidence accuracy remains within acceptable drift bands across the active module portfolio."
            tone="default"
          />
          <MetricCard
            label="Validation rejection rate"
            value={formatPct(1 - validationSummary.platformPassRate)}
            detail="Rejection pressure tracks how aggressively the platform is protecting recommendation integrity."
            tone="warn"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
          <WorkspacePanel
            title="Cross-module performance comparison"
            description="DiamondEdge, GridironEdge, CourtEdge, and PitchEdge compared through a shared performance model."
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-surface-4">
                    <th className="py-3 pr-3">Module</th>
                    <th className="py-3 pr-3">Sport</th>
                    <th className="py-3 pr-3">ROI</th>
                    <th className="py-3 pr-3">CLV</th>
                    <th className="py-3 pr-3">Confidence</th>
                    <th className="py-3 pr-3">Edge realization</th>
                    <th className="py-3">Calibration</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((module, index) => (
                    <tr key={module.id} className="border-b border-surface-4/70 last:border-0 text-gray-300">
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-100">{module.module}</span>
                          {index === 0 ? (
                            <Badge variant="win" size="xs">
                              Leader
                            </Badge>
                          ) : null}
                        </div>
                        <div className="text-xs text-gray-500">{module.league}</div>
                      </td>
                      <td className="py-3 pr-3">{module.sport}</td>
                      <td className="py-3 pr-3 font-mono text-win">{formatPct(module.roi)}</td>
                      <td className="py-3 pr-3 font-mono">{formatPct(module.clv)}</td>
                      <td className="py-3 pr-3 font-mono">{formatPct(module.confidenceAccuracy)}</td>
                      <td className="py-3 pr-3 font-mono">{formatPct(module.edgeRealization)}</td>
                      <td className="py-3 font-mono">{formatPct(module.calibrationScore)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </WorkspacePanel>

          <WorkspacePanel
            title="Executive intelligence views"
            description="Portfolio attribution, module leaderboard context, and AI system summaries for operators."
          >
            <div className="space-y-4">
              <div className="rounded-lg border border-surface-4 p-4 bg-surface-2/60 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
                  <ArrowUpRight className="w-4 h-4 text-brand" />
                  Module performance leaderboard
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {analyticsExecutiveSummary.leaderboardLeader} is converting the most edge this week while keeping CLV expansion above target.
                </p>
              </div>
              <div className="rounded-lg border border-surface-4 p-4 bg-surface-2/60 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
                  <Scale className="w-4 h-4 text-info" />
                  Portfolio attribution analysis
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {analyticsExecutiveSummary.attributionHeadline}
                </p>
              </div>
              <div className="rounded-lg border border-surface-4 p-4 bg-surface-2/60 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
                  <Brain className="w-4 h-4 text-win" />
                  AI system performance summary
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Recommendation confidence drift is modest and remains anchored by a {formatPct(portfolioSummary.weightedClv)} closing line value baseline.
                </p>
              </div>
            </div>
          </WorkspacePanel>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
          <WorkspacePanel
            title="Navyrx signal analytics"
            description="Confidence distribution, drift, contradiction frequency, and recommendation lifecycle signals."
            className="xl:col-span-2"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
                  <Radar className="w-4 h-4 text-brand" />
                  Confidence distribution
                </div>
                {confidenceDistribution.map((bucket) => (
                  <MeterRow
                    key={bucket.label}
                    label={bucket.label}
                    valueLabel={formatPct(bucket.share)}
                    share={bucket.share}
                    tone="brand"
                  />
                ))}
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
                  <Target className="w-4 h-4 text-info" />
                  Confidence drift by module
                </div>
                {confidenceDrift.map((drift) => (
                  <MeterRow
                    key={drift.label}
                    label={drift.label}
                    valueLabel={formatPct(Math.abs(drift.drift))}
                    share={Math.abs(drift.drift) / 0.05}
                    tone={drift.drift < -0.015 ? 'warn' : 'win'}
                    detail={drift.drift < 0 ? 'Confidence is drifting below expectation.' : 'Confidence remains stable or improving.'}
                  />
                ))}
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2 pt-2">
              {recommendationTypeRows.map((row) => (
                <div key={row.type} className="rounded-lg border border-surface-4 p-4 bg-surface-2/60 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-200">{row.type}</span>
                    <span className="font-mono text-xs text-gray-400">{row.volume} recs</span>
                  </div>
                  <MeterRow
                    label="Lifecycle release rate"
                    valueLabel={formatPct(row.releaseRate)}
                    share={row.releaseRate}
                    tone="win"
                    detail="Used for recommendation lifecycle analytics and validation rejection monitoring."
                  />
                </div>
              ))}
            </div>
          </WorkspacePanel>

          <WorkspacePanel
            title="Recommendation-source abstraction"
            description="Cross-sport analytics organized around recommendation type rather than any single module."
          >
            <div className="space-y-4">
              {recommendationTypeRows.map((row) => (
                <div key={row.type} className="rounded-lg border border-surface-4 p-4 bg-surface-2/60 space-y-2">
                  <div className="text-sm font-medium text-gray-200">{row.type}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Supports performance by recommendation type, market category, and timeframe without MLB-specific assumptions.
                  </p>
                  <div className="font-mono text-xs text-gray-400">Volume {row.volume}</div>
                </div>
              ))}
            </div>
          </WorkspacePanel>
        </div>
      </div>
    </div>
  );
}
